import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import startCase from 'lodash/startCase';
import { Box, Heading, Flex, Text, Icon } from '@qga/roo-ui/components';
import { getFullKnownQuery, getQueryMinPrice, getQueryMaxPrice } from 'store/router/routerSelectors';
import { clearFilters, updateQuery } from 'store/search/searchActions';
import { useDataLayer } from 'hooks/useDataLayer';
import { MessageContainer, RemoveFilterButton, ResetFilterLinkButton } from './primitives';
import { getActiveNonPriceFilterProps } from './lib/getActiveNonPriceFilterProps';

const NoResultsMessage = () => {
  const dispatch = useDispatch();
  const { emitInteractionEvent } = useDataLayer();
  const minPrice = useSelector(getQueryMinPrice);
  const maxPrice = useSelector(getQueryMaxPrice);
  const query = useSelector(getFullKnownQuery);
  const activeNonPriceFiltersProps = useMemo(() => getActiveNonPriceFilterProps(query), [query]);

  const onRemoveFilterClick = useCallback(
    (filterType, filterValue) => {
      dispatch(updateQuery({ [filterType]: filterValue }));
      emitInteractionEvent({ type: 'No Search Results', value: `Removed ${startCase(filterType)} filter` });
    },
    [dispatch, emitInteractionEvent],
  );

  const onResetAllFilters = useCallback(() => {
    dispatch(clearFilters());
    emitInteractionEvent({ type: 'No Search Results', value: 'Removed all filters' });
  }, [dispatch, emitInteractionEvent]);

  const onResetPrices = useCallback(() => {
    dispatch(updateQuery({ minPrice: undefined, maxPrice: undefined }));
    emitInteractionEvent({ type: 'No Search Results', value: 'Removed price filters' });
  }, [dispatch, emitInteractionEvent]);

  const focusOnLocationInput = useCallback(() => {
    document.querySelector('[data-testid="location-search-input"]').focus();
    emitInteractionEvent({ type: 'No Search Results', value: 'Changed location' });
  }, [emitInteractionEvent]);

  const focusOnDatePicker = useCallback(() => {
    document.querySelector('[data-testid="select-date-input"]').focus();
    emitInteractionEvent({ type: 'No Search Results', value: 'Changed stay dates' });
  }, [emitInteractionEvent]);

  return (
    <MessageContainer px={[5, 11]} py={[6, 9]}>
      <Heading.h3>Sorry we can&apos;t find any results</Heading.h3>
      <Text fontSize="base" data-testid="no-results-copy">
        Try changing your filters, adjust your travel dates or search properties in a different area
      </Text>
      <Flex flexWrap="wrap" mt={4} mb={2} data-testid="remove-filter-buttons">
        {!isEmpty(activeNonPriceFiltersProps) && (
          <RemoveFilterButton
            key="reset-all-filters-button"
            data-testid="reset-all-filters-button"
            icon="close"
            onClick={onResetAllFilters}
          >
            <Flex alignItems="center" justifyContent="center">
              <Text fontSize="base">Reset All Filters</Text>
            </Flex>
          </RemoveFilterButton>
        )}
        {activeNonPriceFiltersProps.map(({ id, title, filterType, filterValue }) => (
          <RemoveFilterButton
            key={`clear-${title}-button`}
            data-testid={`clear-${id}-button`}
            icon="close"
            onClick={() => onRemoveFilterClick(filterType, filterValue)}
            aria-label={title}
          >
            <Flex alignItems="center" justifyContent="center">
              <Text fontSize="base">{title}</Text>
              <Icon name="close" size={16} ml={4} />
            </Flex>
          </RemoveFilterButton>
        ))}
      </Flex>
      <Box data-testid="edit-query-buttons">
        {(minPrice || maxPrice) && (
          <ResetFilterLinkButton data-testid="reset-prices-button" onClick={onResetPrices}>
            Reset price per night
          </ResetFilterLinkButton>
        )}
        <ResetFilterLinkButton data-testid="change-destination-button" onClick={focusOnLocationInput}>
          Change destination
        </ResetFilterLinkButton>
        <ResetFilterLinkButton data-testid="change-dates-button" onClick={focusOnDatePicker}>
          Change dates
        </ResetFilterLinkButton>
      </Box>
    </MessageContainer>
  );
};

export default NoResultsMessage;
